import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { styled } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import ErrorIcon from "@mui/icons-material/Error";
import ReplayIcon from "@mui/icons-material/Replay";
import Divider from "@mui/material/Divider";
import { blue, green, grey, red } from "designSystem/styles/color";
import { Button, FlexColumn, FlexRowCenter, IconButton, Text, TextField } from "designSystem/index";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { useHiggSetupStore } from "./HiggSetupStore";
import LinearProgress, { linearProgressClasses } from "@mui/material/LinearProgress";

const PREFIX = "InitializeHigg";

const classes = {
	apiLinkText: `${PREFIX}-apiLinkText`,
	checkCircleIcon: `${PREFIX}-checkCircleIcon`,
	editApiKeyButton: `${PREFIX}-editApiKeyButton`,
	errorIcon: `${PREFIX}-errorIcon`,
	higgInitFailText: `${PREFIX}-higgInitFailText`,
	largeFontSize: `${PREFIX}-largeFontSize`,
	standardFontSize: `${PREFIX}-standardFontSize`,
};

const StyledFlexColumn = styled(FlexColumn)((
	{
		theme,
	},
) => ({
	[`& .${classes.apiLinkText}`]: {
		width: 60,
	},

	[`& .${classes.checkCircleIcon}`]: {
		color: green[600],
	},

	[`& .${classes.editApiKeyButton}`]: {
		borderColor: grey[400],
		borderRadius: 3,
	},

	[`& .${classes.errorIcon}`]: {
		color: red[600],
	},

	[`& .${classes.higgInitFailText}`]: {
		color: red[600],
		position: "absolute",
		top: 18,
	},

	[`& .${classes.largeFontSize}`]: {
		fontSize: 16,
	},

	[`& .${classes.standardFontSize}`]: {
		fontSize: 12,
	},
}));

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
	borderRadius: 5,
	height: 12,
	width: 277,
	[`& .${linearProgressClasses.bar}`]: {
		borderRadius: 5,
	},
	[`& .${linearProgressClasses.barColorPrimary}`]: {
		backgroundColor: blue[600],
	},
	[`& .${linearProgressClasses.barColorSecondary}`]: {
		backgroundColor: red[600],
	},
	[`& .${linearProgressClasses.colorPrimary}`]: {
		backgroundColor: grey[400],
	},
	[`& .${linearProgressClasses.colorSecondary}`]: {
		backgroundColor: grey[400],
	},	
}));

const InitializeHigg: FunctionComponent = observer(() => {
	const {
		apiKeyValue,
		disableInitializeButton,
		higgDoneLoading,
		higgLoadProgress,
		initializeHiggCategoriesAndMaterials,
		isHiggError,
		onOpenApiDialog,
		showHiggIndeterminateLoader,
	} = useHiggSetupStore();

	const {
		apiLinkText,
		checkCircleIcon,
		editApiKeyButton,
		errorIcon,
		higgInitFailText,
		largeFontSize,
		standardFontSize,
	} = classes;

	return (
		<StyledFlexColumn
			flexGrow={1}
			gap={6}
			m={4}
			width="100%"
		>
			<Text
				bold
				className={largeFontSize}
				res="ConnectionToHiggAPI"
			/>
			<FlexRowCenter
				flexGrow={1}
				gap={2}
				maxWidth={550}
			>
				<Text
					bold
					className={apiLinkText}
					res="APILink"
				/>
				<TextField
					InputProps={{ type: "password" }}
					contentEditable={false}
					fullWidth
					value={apiKeyValue}
				/>
				<IconButton
					className={editApiKeyButton}
					design="outline"
					onClick={onOpenApiDialog}
					res="Edit"
				>
					<EditIcon />
				</IconButton>
			</FlexRowCenter>
			<Divider />
			<FlexRowCenter
				flexGrow={1}
				justifyContent="space-between"
				maxWidth={550}
			>
				<Text
					bold
					className={largeFontSize}
					res="ConnectionToHiggMSIdatabase"
				/>
				<Button
					design="primary"
					disabled={disableInitializeButton}
					onClick={initializeHiggCategoriesAndMaterials}
					res="Initialize"
				/>
			</FlexRowCenter>
			<FlexRowCenter
				flexGrow={1}
				gap={3}
				height={50}
				justifyContent="space-between"
				maxWidth={550}
			>
				<FlexRowCenter
					flexGrow={1}
					justifyContent="space-between"
					position="relative"
				>
					<Text
						bold
						className={standardFontSize}
						res="HiggInitializationProcess"
					/>
					<FlexColumn>
						{showHiggIndeterminateLoader ?
							<BorderLinearProgress variant="indeterminate" />
							: (
								<BorderLinearProgress
									color={isHiggError ? "secondary" : "primary"}
									value={higgLoadProgress}
									variant="determinate"
								/>
							)}
						{isHiggError && (
							<Text
								className={higgInitFailText}
								res="HiggInitializationFailed"
							/>
						)}
					</FlexColumn>
				</FlexRowCenter>
				<FlexRowCenter
					gap={2}
					width={50}
				>
					{isHiggError ? (
						<>
							<ErrorIcon
								className={errorIcon}
								fontSize="large"
							/>
							<IconButton
								onClick={initializeHiggCategoriesAndMaterials}
								size="large"
							>
								<ReplayIcon />
							</IconButton>
						</>
					)
						: (
							<CheckCircleIcon
								className={checkCircleIcon}
								fontSize="large"
								style={{ visibility: higgDoneLoading ? "visible" : "hidden" }}
							/>
						)}
				</FlexRowCenter>
			</FlexRowCenter>
		</StyledFlexColumn>
	);
});

export default InitializeHigg;